import { TribeBreakdown, DriverBreakdown, PatternBreakdown } from "./tribes";

import parisRunnersImg from "@/assets/communities/paris-runners.jpg";
import culinaryLyonImg from "@/assets/communities/culinary-lyon.jpg";
import chessParisImg from "@/assets/communities/chess-paris.jpg";
import yogaParisImg from "@/assets/communities/yoga-paris.jpg";
import streetArtMarseilleImg from "@/assets/communities/street-art-marseille.jpg";
import techBordeauxImg from "@/assets/communities/tech-bordeaux.jpg";
import sustainableNantesImg from "@/assets/communities/sustainable-nantes.jpg";
import bookClubToulouseImg from "@/assets/communities/book-club-toulouse.jpg";
import cyclingLyonImg from "@/assets/communities/cycling-lyon.jpg";
import photographyParisImg from "@/assets/communities/photography-paris.jpg";
import coffeeMarseilleImg from "@/assets/communities/coffee-marseille.jpg";
import entrepreneursParisImg from "@/assets/communities/entrepreneurs-paris.jpg";

export type EventType = "Run" | "Meetup" | "Workshop" | "Class" | "Social" | "Competition" | "Retreat";
export type EventVibe = "Social" | "Performance" | "Educational" | "Experiential";
export type EventAttendance = "Small" | "Medium" | "Large";

export type GovernanceModel = "Founder-Led" | "Collective" | "Member-Led" | "Brand-Owned" | "Institution-Backed" | "Association-Led";
export type CommunityStage = "Emerging" | "Growing" | "Established";
export type PlatformName = "Instagram" | "Strava" | "WhatsApp" | "Reddit" | "Discord" | "Telegram" | "Slack" | "Facebook" | "TikTok" | "YouTube" | "LinkedIn" | "Meetup" | "X";

export interface UpcomingEvent {
  id: string;
  name: string;
  type: EventType;
  date: string;
  attendance: EventAttendance;
  vibe: EventVibe;
  openToBrands: boolean;
}

export interface Community {
  id: string;
  name: string;
  location: string;
  geographicScope: string;
  governanceModel: GovernanceModel;
  stage: CommunityStage;
  platforms: PlatformName[];
  activeMembers: number;
  totalReach: number;
  networkDensity: string;
  avgEventAttendance: string;
  eventsPerYear: number;
  theme: string[];
  memberCount: number;
  fitScore: number;
  participationRate: number;
  eventFrequency: string;
  description: string;
  mission: string;
  demographics: {
    ageRange: string;
    genderRatio: string;
    interests: string[];
  };
  activationCapabilities: string[];
  typicalEventSize: string;
  leadTime: string;
  budgetRange: string;
  eventsHosted: number;
  brandCollaborations: number;
  image: string;
  memberDrivers: string[];
  communityValues: string[];
  tribes: TribeBreakdown[];
  intentionDrivers: DriverBreakdown[];
  consumptionPatterns: PatternBreakdown[];
  upcomingEvents?: UpcomingEvent[];
  pastCollaborations?: PastCollaboration[];
}

export interface PastCollaboration {
  brand: string;
  activationType: string;
  date: string;
  result: string;
}

export const communities: Community[] = [
  {
    id: "paris-runners-club",
    name: "Paris Runners Club",
    location: "Paris",
    geographicScope: "City-wide",
    governanceModel: "Founder-Led",
    stage: "Established",
    platforms: ["Instagram", "Strava", "WhatsApp"],
    activeMembers: 350,
    totalReach: 12000,
    networkDensity: "840K",
    avgEventAttendance: "75–200",
    eventsPerYear: 96,
    theme: ["Road Running", "Trail Running"],
    memberCount: 850,
    fitScore: 92,
    participationRate: 78,
    eventFrequency: "Weekly",
    description: "The premier running community in Paris, bringing together passionate runners of all levels.",
    mission: "We believe running is more than exercise—it's a way to connect, explore the city, and build lasting friendships.",
    demographics: { ageRange: "25-40", genderRatio: "55% F / 45% M", interests: ["Fitness", "Wellness", "Outdoor activities", "Social events"] },
    activationCapabilities: ["Event Sponsorship", "Product Gifting", "Co-Creation Workshops", "Product Testing", "Ambassador Program"],
    typicalEventSize: "60-120",
    leadTime: "3-4 weeks",
    budgetRange: "€2,000-€5,000",
    eventsHosted: 48,
    brandCollaborations: 8,
    image: parisRunnersImg,
    memberDrivers: ["Connection & Belonging", "Practice & Discipline", "Progress & Growth"],
    communityValues: ["Performance & Excellence", "Inclusivity & Community", "Authenticity & Transparency"],
    tribes: [
      { tribe: "Sport", percentage: 70, microTribes: [{ name: "Road Running", percentage: 45 }, { name: "Trail Running", percentage: 30 }, { name: "Endurance Sports", percentage: 25 }] },
      { tribe: "Wellness", percentage: 20, microTribes: [{ name: "Fitness", percentage: 65 }, { name: "Retreats", percentage: 35 }] },
      { tribe: "Lifestyle", percentage: 10, microTribes: [{ name: "Health", percentage: 60 }, { name: "Mental Health", percentage: 40 }] },
    ],
    intentionDrivers: [
      { name: "Performance & Improvement", percentage: 40 },
      { name: "Balance & Well-being", percentage: 25 },
      { name: "Identity & Belonging", percentage: 20 },
      { name: "Exploration & Novelty", percentage: 15 },
    ],
    consumptionPatterns: [
      { name: "Habit-Driven", percentage: 30 },
      { name: "Premium", percentage: 25 },
      { name: "Social", percentage: 20 },
      { name: "Aspirational", percentage: 15 },
      { name: "Functional", percentage: 10 },
    ],
    upcomingEvents: [
      { id: "evt-001", name: "Sunday Long Run", type: "Run", date: "2025-03-16", attendance: "Medium", vibe: "Performance", openToBrands: true },
      { id: "evt-002", name: "Community Brunch", type: "Social", date: "2025-03-22", attendance: "Small", vibe: "Social", openToBrands: true },
      { id: "evt-003", name: "Interval Training", type: "Run", date: "2025-03-29", attendance: "Small", vibe: "Performance", openToBrands: false },
      { id: "evt-004", name: "Trail Discovery", type: "Run", date: "2025-04-05", attendance: "Medium", vibe: "Experiential", openToBrands: true },
    ],
    pastCollaborations: [
      { brand: "HOKA", activationType: "Event Sponsorship", date: "Jan 2025", result: "92% satisfaction, 340 product trials" },
      { brand: "Asphalte", activationType: "Product Testing", date: "Nov 2024", result: "78 testers, 4.6/5 avg rating" },
      { brand: "Swile", activationType: "Co-Creation Workshops", date: "Sep 2024", result: "120 participants, 3 new product ideas" },
      { brand: "Feed.", activationType: "Product Gifting", date: "Jun 2024", result: "200 samples distributed, 62% repurchase" },
    ],
  },
  {
    id: "le-cercle-culinaire",
    name: "Le Cercle Culinaire",
    location: "Lyon",
    geographicScope: "Regional",
    governanceModel: "Collective",
    stage: "Growing",
    platforms: ["Instagram", "WhatsApp", "Facebook"],
    activeMembers: 180,
    totalReach: 5200,
    networkDensity: "320K",
    avgEventAttendance: "30–50",
    eventsPerYear: 24,
    theme: ["Fine Dining", "Wine", "Home Cooking"],
    memberCount: 320,
    fitScore: 78,
    participationRate: 72,
    eventFrequency: "Monthly",
    description: "A passionate community of food lovers exploring Lyon's culinary heritage.",
    mission: "To preserve and celebrate Lyon's gastronomic traditions while embracing innovation.",
    demographics: { ageRange: "30-50", genderRatio: "60% F / 40% M", interests: ["Cooking", "Wine", "Local produce", "Gastronomy"] },
    activationCapabilities: ["Event Sponsorship", "Product Gifting", "Co-Creation Workshops", "Branded Content", "Product Testing"],
    typicalEventSize: "30-50",
    leadTime: "4-6 weeks",
    budgetRange: "€1,500-€3,500",
    eventsHosted: 24,
    brandCollaborations: 5,
    image: culinaryLyonImg,
    memberDrivers: ["Practice & Discipline", "Connection & Belonging", "Expression & Identity"],
    communityValues: ["Authenticity & Transparency", "Sustainability & Ethics", "Luxury & Exclusivity"],
    tribes: [
      { tribe: "Food", percentage: 75, microTribes: [{ name: "Fine Dining", percentage: 30 }, { name: "Home Cooking", percentage: 30 }, { name: "Wine", percentage: 25 }, { name: "Plant Based Diets", percentage: 15 }] },
      { tribe: "Culture", percentage: 20, microTribes: [{ name: "History", percentage: 60 }, { name: "Literature", percentage: 40 }] },
      { tribe: "Lifestyle", percentage: 5, microTribes: [{ name: "Health", percentage: 100 }] },
    ],
    intentionDrivers: [
      { name: "Exploration & Novelty", percentage: 35 },
      { name: "Identity & Belonging", percentage: 30 },
      { name: "Balance & Well-being", percentage: 20 },
      { name: "Impact & Ethics", percentage: 15 },
    ],
    consumptionPatterns: [
      { name: "Premium", percentage: 35 },
      { name: "Experimental", percentage: 25 },
      { name: "Social", percentage: 20 },
      { name: "Aspirational", percentage: 20 },
    ],
    pastCollaborations: [
      { brand: "Maison Chapoutier", activationType: "Co-Creation Workshops", date: "Dec 2024", result: "45 participants, wine-pairing guide created" },
      { brand: "Le Creuset", activationType: "Product Gifting", date: "Oct 2024", result: "50 gifts, 88% social media share rate" },
      { brand: "Typology", activationType: "Event Sponsorship", date: "Jul 2024", result: "80 attendees, brand awareness +35%" },
    ],
  },
  {
    id: "chess-collective",
    name: "Chess Collective",
    location: "Paris",
    geographicScope: "City-wide",
    governanceModel: "Member-Led",
    stage: "Growing",
    platforms: ["Discord", "Instagram"],
    activeMembers: 95,
    totalReach: 2800,
    networkDensity: "180K",
    avgEventAttendance: "20–40",
    eventsPerYear: 96,
    theme: ["Gaming & E-sports", "Literature"],
    memberCount: 180,
    fitScore: 65,
    participationRate: 68,
    eventFrequency: "Weekly",
    description: "A vibrant chess community bringing together enthusiasts from all backgrounds.",
    mission: "Making chess accessible and fun for everyone in iconic Parisian cafés.",
    demographics: { ageRange: "20-55", genderRatio: "35% F / 65% M", interests: ["Strategy games", "Intellectual pursuits", "Café culture"] },
    activationCapabilities: ["Event Sponsorship"],
    typicalEventSize: "20-40",
    leadTime: "2-3 weeks",
    budgetRange: "€500-€1,500",
    eventsHosted: 96,
    brandCollaborations: 2,
    image: chessParisImg,
    memberDrivers: ["Practice & Discipline", "Progress & Growth", "Connection & Belonging"],
    communityValues: ["Inclusivity & Community", "Performance & Excellence"],
    tribes: [
      { tribe: "Culture", percentage: 55, microTribes: [{ name: "Gaming & E-sports", percentage: 50 }, { name: "Literature", percentage: 30 }, { name: "History", percentage: 20 }] },
      { tribe: "Learning & Growth", percentage: 35, microTribes: [{ name: "Skill Building", percentage: 60 }, { name: "Personal Development", percentage: 40 }] },
      { tribe: "Relationships & Social Life", percentage: 10, microTribes: [{ name: "Social Events", percentage: 100 }] },
    ],
    intentionDrivers: [
      { name: "Performance & Improvement", percentage: 45 },
      { name: "Identity & Belonging", percentage: 25 },
      { name: "Exploration & Novelty", percentage: 20 },
      { name: "Balance & Well-being", percentage: 10 },
    ],
    consumptionPatterns: [
      { name: "Habit-Driven", percentage: 35 },
      { name: "Social", percentage: 30 },
      { name: "Pragmatic", percentage: 20 },
      { name: "Functional", percentage: 15 },
    ],
  },
  {
    id: "yoga-mindfulness-paris",
    name: "Yoga & Mindfulness Paris",
    location: "Paris",
    geographicScope: "City-wide",
    governanceModel: "Founder-Led",
    stage: "Established",
    platforms: ["Instagram", "WhatsApp", "YouTube"],
    activeMembers: 280,
    totalReach: 9500,
    networkDensity: "620K",
    avgEventAttendance: "40–80",
    eventsPerYear: 52,
    theme: ["Yoga", "Meditation", "Retreats"],
    memberCount: 540,
    fitScore: 88,
    participationRate: 75,
    eventFrequency: "Weekly",
    description: "Paris's leading wellness community focused on yoga and mindfulness practices.",
    mission: "Creating space for inner peace in the city through yoga, meditation retreats, and wellness workshops.",
    demographics: { ageRange: "25-45", genderRatio: "75% F / 25% M", interests: ["Yoga", "Meditation", "Healthy living", "Self-improvement"] },
    activationCapabilities: ["Event Sponsorship", "Product Gifting", "Co-Creation Workshops", "Branded Content", "Product Testing", "Ambassador Program"],
    typicalEventSize: "40-80",
    leadTime: "3-4 weeks",
    budgetRange: "€2,000-€4,000",
    eventsHosted: 52,
    brandCollaborations: 6,
    image: yogaParisImg,
    memberDrivers: ["Practice & Discipline", "Meaning & Contribution", "Connection & Belonging"],
    communityValues: ["Sustainability & Ethics", "Authenticity & Transparency", "Inclusivity & Community"],
    tribes: [
      { tribe: "Wellness", percentage: 80, microTribes: [{ name: "Yoga", percentage: 45 }, { name: "Meditation", percentage: 30 }, { name: "Retreats", percentage: 15 }, { name: "Fitness", percentage: 10 }] },
      { tribe: "Lifestyle", percentage: 15, microTribes: [{ name: "Mental Health", percentage: 60 }, { name: "Health", percentage: 40 }] },
      { tribe: "Travel & Adventure", percentage: 5, microTribes: [{ name: "Cultural Travel", percentage: 100 }] },
    ],
    intentionDrivers: [
      { name: "Balance & Well-being", percentage: 45 },
      { name: "Identity & Belonging", percentage: 25 },
      { name: "Impact & Ethics", percentage: 15 },
      { name: "Exploration & Novelty", percentage: 15 },
    ],
    consumptionPatterns: [
      { name: "Habit-Driven", percentage: 30 },
      { name: "Premium", percentage: 25 },
      { name: "Aspirational", percentage: 20 },
      { name: "Social", percentage: 15 },
      { name: "Functional", percentage: 10 },
    ],
    pastCollaborations: [
      { brand: "Lululemon", activationType: "Ambassador Program", date: "Feb 2025", result: "12 ambassadors, 25K social reach" },
      { brand: "Typology", activationType: "Product Gifting", date: "Oct 2024", result: "100 kits, 91% satisfaction" },
    ],
  },
  {
    id: "street-art-walks",
    name: "Street Art Walks",
    location: "Marseille",
    geographicScope: "City-wide",
    governanceModel: "Collective",
    stage: "Emerging",
    platforms: ["Instagram", "TikTok"],
    activeMembers: 120,
    totalReach: 4500,
    networkDensity: "210K",
    avgEventAttendance: "25–45",
    eventsPerYear: 18,
    theme: ["Contemporary Art", "Design", "Architecture"],
    memberCount: 210,
    fitScore: 71,
    participationRate: 65,
    eventFrequency: "Monthly",
    description: "Exploring Marseille's vibrant street art scene through guided walks and artist meetups.",
    mission: "To showcase the incredible street art culture of Marseille and connect people with local artists.",
    demographics: { ageRange: "22-40", genderRatio: "50% F / 50% M", interests: ["Art", "Photography", "Urban culture", "Local history"] },
    activationCapabilities: ["Event Sponsorship", "Product Gifting", "Co-Creation Workshops", "Branded Content"],
    typicalEventSize: "25-45",
    leadTime: "2-4 weeks",
    budgetRange: "€1,000-€2,500",
    eventsHosted: 18,
    brandCollaborations: 3,
    image: streetArtMarseilleImg,
    memberDrivers: ["Expression & Identity", "Connection & Belonging", "Access & Opportunity"],
    communityValues: ["Authenticity & Transparency", "Inclusivity & Community", "Innovation & Progress"],
    tribes: [
      { tribe: "Culture", percentage: 70, microTribes: [{ name: "Contemporary Art", percentage: 50 }, { name: "Design", percentage: 25 }, { name: "Architecture", percentage: 15 }, { name: "History", percentage: 10 }] },
      { tribe: "Relationships & Social Life", percentage: 20, microTribes: [{ name: "Social Events", percentage: 100 }] },
      { tribe: "Travel & Adventure", percentage: 10, microTribes: [{ name: "Cultural Travel", percentage: 100 }] },
    ],
    intentionDrivers: [
      { name: "Exploration & Novelty", percentage: 40 },
      { name: "Identity & Belonging", percentage: 30 },
      { name: "Impact & Ethics", percentage: 20 },
      { name: "Balance & Well-being", percentage: 10 },
    ],
    consumptionPatterns: [
      { name: "Experimental", percentage: 30 },
      { name: "Social", percentage: 25 },
      { name: "Aspirational", percentage: 20 },
      { name: "Pragmatic", percentage: 15 },
      { name: "Functional", percentage: 10 },
    ],
  },
  {
    id: "tech-meetup-bordeaux",
    name: "Tech Meetup Bordeaux",
    location: "Bordeaux",
    geographicScope: "Regional",
    governanceModel: "Association-Led",
    stage: "Established",
    platforms: ["Slack", "LinkedIn", "Meetup"],
    activeMembers: 320,
    totalReach: 15000,
    networkDensity: "1.2M",
    avgEventAttendance: "80–150",
    eventsPerYear: 36,
    theme: ["Startups", "Artificial Intelligence", "Product & Innovation"],
    memberCount: 680,
    fitScore: 82,
    participationRate: 70,
    eventFrequency: "Monthly",
    description: "Bordeaux's largest tech community, connecting developers, designers, and entrepreneurs.",
    mission: "Building a thriving tech ecosystem in Bordeaux through talks, workshops, and networking events.",
    demographics: { ageRange: "25-45", genderRatio: "30% F / 70% M", interests: ["Technology", "Startups", "Programming", "Innovation"] },
    activationCapabilities: ["Event Sponsorship", "Product Gifting", "Branded Content", "Product Testing", "Affiliate Program"],
    typicalEventSize: "80-150",
    leadTime: "4-6 weeks",
    budgetRange: "€3,000-€6,000",
    eventsHosted: 36,
    brandCollaborations: 7,
    image: techBordeauxImg,
    memberDrivers: ["Progress & Growth", "Access & Opportunity", "Connection & Belonging"],
    communityValues: ["Innovation & Progress", "Performance & Excellence", "Inclusivity & Community"],
    tribes: [
      { tribe: "Tech", percentage: 75, microTribes: [{ name: "Startups", percentage: 35 }, { name: "Artificial Intelligence", percentage: 30 }, { name: "Product & Innovation", percentage: 20 }, { name: "Web3", percentage: 15 }] },
      { tribe: "Learning & Growth", percentage: 15, microTribes: [{ name: "Skill Building", percentage: 50 }, { name: "Career Development", percentage: 30 }, { name: "Personal Development", percentage: 20 }] },
      { tribe: "Relationships & Social Life", percentage: 10, microTribes: [{ name: "Social Events", percentage: 100 }] },
    ],
    intentionDrivers: [
      { name: "Performance & Improvement", percentage: 35 },
      { name: "Exploration & Novelty", percentage: 30 },
      { name: "Identity & Belonging", percentage: 20 },
      { name: "Impact & Ethics", percentage: 15 },
    ],
    consumptionPatterns: [
      { name: "Pragmatic", percentage: 25 },
      { name: "Experimental", percentage: 25 },
      { name: "Aspirational", percentage: 20 },
      { name: "Premium", percentage: 15 },
      { name: "Functional", percentage: 15 },
    ],
  },
  {
    id: "sustainable-living-nantes",
    name: "Sustainable Living Nantes",
    location: "Nantes",
    geographicScope: "City-wide",
    governanceModel: "Member-Led",
    stage: "Growing",
    platforms: ["Instagram", "Facebook", "WhatsApp"],
    activeMembers: 220,
    totalReach: 6800,
    networkDensity: "380K",
    avgEventAttendance: "35–60",
    eventsPerYear: 44,
    theme: ["Health", "Plant Based Diets"],
    memberCount: 420,
    fitScore: 85,
    participationRate: 73,
    eventFrequency: "Weekly",
    description: "A community dedicated to sustainable living practices and environmental awareness.",
    mission: "Making sustainable living practical and accessible through workshops, swaps, and community gardens.",
    demographics: { ageRange: "28-50", genderRatio: "65% F / 35% M", interests: ["Sustainability", "Zero waste", "Gardening", "DIY"] },
    activationCapabilities: ["Event Sponsorship", "Product Gifting", "Co-Creation Workshops", "Product Testing", "Affiliate Program"],
    typicalEventSize: "35-60",
    leadTime: "3-4 weeks",
    budgetRange: "€1,500-€3,000",
    eventsHosted: 44,
    brandCollaborations: 4,
    image: sustainableNantesImg,
    memberDrivers: ["Meaning & Contribution", "Practice & Discipline", "Connection & Belonging"],
    communityValues: ["Sustainability & Ethics", "Authenticity & Transparency", "Inclusivity & Community"],
    tribes: [
      { tribe: "Lifestyle", percentage: 50, microTribes: [{ name: "Health", percentage: 40 }, { name: "Mental Health", percentage: 30 }, { name: "Parenting", percentage: 30 }] },
      { tribe: "Food", percentage: 30, microTribes: [{ name: "Plant Based Diets", percentage: 50 }, { name: "Home Cooking", percentage: 30 }, { name: "Wine", percentage: 20 }] },
      { tribe: "Learning & Growth", percentage: 20, microTribes: [{ name: "Personal Development", percentage: 50 }, { name: "Skill Building", percentage: 50 }] },
    ],
    intentionDrivers: [
      { name: "Impact & Ethics", percentage: 40 },
      { name: "Balance & Well-being", percentage: 30 },
      { name: "Identity & Belonging", percentage: 20 },
      { name: "Exploration & Novelty", percentage: 10 },
    ],
    consumptionPatterns: [
      { name: "Pragmatic", percentage: 30 },
      { name: "Habit-Driven", percentage: 25 },
      { name: "Functional", percentage: 20 },
      { name: "Social", percentage: 15 },
      { name: "Experimental", percentage: 10 },
    ],
  },
  {
    id: "book-club-toulouse",
    name: "The Toulouse Book Club",
    location: "Toulouse",
    geographicScope: "City-wide",
    governanceModel: "Founder-Led",
    stage: "Growing",
    platforms: ["Instagram", "WhatsApp"],
    activeMembers: 85,
    totalReach: 2200,
    networkDensity: "140K",
    avgEventAttendance: "20–35",
    eventsPerYear: 24,
    theme: ["Literature", "History"],
    memberCount: 150,
    fitScore: 62,
    participationRate: 80,
    eventFrequency: "Monthly",
    description: "A passionate community of book lovers meeting in Toulouse's charming cafés.",
    mission: "Bringing together readers to share ideas, discover new authors, and build meaningful connections through literature.",
    demographics: { ageRange: "30-60", genderRatio: "70% F / 30% M", interests: ["Reading", "Literature", "Discussion", "Cultural events"] },
    activationCapabilities: ["Event Sponsorship", "Product Gifting"],
    typicalEventSize: "20-35",
    leadTime: "2-3 weeks",
    budgetRange: "€500-€1,500",
    eventsHosted: 24,
    brandCollaborations: 1,
    image: bookClubToulouseImg,
    memberDrivers: ["Connection & Belonging", "Practice & Discipline", "Expression & Identity"],
    communityValues: ["Authenticity & Transparency", "Inclusivity & Community"],
    tribes: [
      { tribe: "Culture", percentage: 65, microTribes: [{ name: "Literature", percentage: 70 }, { name: "History", percentage: 20 }, { name: "Film", percentage: 10 }] },
      { tribe: "Learning & Growth", percentage: 25, microTribes: [{ name: "Personal Development", percentage: 50 }, { name: "Language Learning", percentage: 30 }, { name: "Skill Building", percentage: 20 }] },
      { tribe: "Relationships & Social Life", percentage: 10, microTribes: [{ name: "Social Events", percentage: 100 }] },
    ],
    intentionDrivers: [
      { name: "Identity & Belonging", percentage: 35 },
      { name: "Exploration & Novelty", percentage: 30 },
      { name: "Balance & Well-being", percentage: 20 },
      { name: "Impact & Ethics", percentage: 15 },
    ],
    consumptionPatterns: [
      { name: "Habit-Driven", percentage: 35 },
      { name: "Social", percentage: 25 },
      { name: "Pragmatic", percentage: 20 },
      { name: "Functional", percentage: 20 },
    ],
  },
  {
    id: "cycling-collective-lyon",
    name: "Cycling Collective Lyon",
    location: "Lyon",
    geographicScope: "Regional",
    governanceModel: "Founder-Led",
    stage: "Established",
    platforms: ["Strava", "Instagram", "WhatsApp"],
    activeMembers: 200,
    totalReach: 7200,
    networkDensity: "520K",
    avgEventAttendance: "40–80",
    eventsPerYear: 52,
    theme: ["Cycling", "Outdoor Sports", "Endurance Sports"],
    memberCount: 380,
    fitScore: 79,
    participationRate: 71,
    eventFrequency: "Weekly",
    description: "Lyon's most active cycling community for road cyclists and urban riders.",
    mission: "Promoting cycling as a sustainable, healthy, and social activity.",
    demographics: { ageRange: "25-50", genderRatio: "40% F / 60% M", interests: ["Cycling", "Fitness", "Sustainability", "Outdoor activities"] },
    activationCapabilities: ["Event Sponsorship", "Product Gifting", "Co-Creation Workshops", "Branded Content", "Product Testing", "Ambassador Program"],
    typicalEventSize: "40-80",
    leadTime: "3-4 weeks",
    budgetRange: "€2,000-€4,500",
    eventsHosted: 52,
    brandCollaborations: 5,
    image: cyclingLyonImg,
    memberDrivers: ["Practice & Discipline", "Progress & Growth", "Connection & Belonging"],
    communityValues: ["Performance & Excellence", "Sustainability & Ethics", "Inclusivity & Community"],
    tribes: [
      { tribe: "Sport", percentage: 70, microTribes: [{ name: "Cycling", percentage: 50 }, { name: "Outdoor Sports", percentage: 25 }, { name: "Endurance Sports", percentage: 25 }] },
      { tribe: "Wellness", percentage: 15, microTribes: [{ name: "Fitness", percentage: 70 }, { name: "Retreats", percentage: 30 }] },
      { tribe: "Travel & Adventure", percentage: 15, microTribes: [{ name: "Adventure Travel", percentage: 60 }, { name: "Backpacking", percentage: 40 }] },
    ],
    intentionDrivers: [
      { name: "Performance & Improvement", percentage: 40 },
      { name: "Balance & Well-being", percentage: 25 },
      { name: "Identity & Belonging", percentage: 20 },
      { name: "Exploration & Novelty", percentage: 15 },
    ],
    consumptionPatterns: [
      { name: "Habit-Driven", percentage: 30 },
      { name: "Premium", percentage: 25 },
      { name: "Functional", percentage: 20 },
      { name: "Aspirational", percentage: 15 },
      { name: "Pragmatic", percentage: 10 },
    ],
  },
  {
    id: "photography-paris",
    name: "Photography Paris",
    location: "Paris",
    geographicScope: "City-wide",
    governanceModel: "Collective",
    stage: "Growing",
    platforms: ["Instagram", "Discord", "Meetup"],
    activeMembers: 150,
    totalReach: 5800,
    networkDensity: "340K",
    avgEventAttendance: "30–50",
    eventsPerYear: 40,
    theme: ["Contemporary Art", "Film", "Design"],
    memberCount: 290,
    fitScore: 76,
    participationRate: 68,
    eventFrequency: "Weekly",
    description: "A community of photographers exploring Paris through their lenses.",
    mission: "To connect photographers of all levels, share knowledge, and capture the beauty of Paris together.",
    demographics: { ageRange: "22-45", genderRatio: "45% F / 55% M", interests: ["Photography", "Art", "Travel", "Urban exploration"] },
    activationCapabilities: ["Event Sponsorship", "Product Gifting", "Co-Creation Workshops", "Branded Content", "Product Testing"],
    typicalEventSize: "30-50",
    leadTime: "2-3 weeks",
    budgetRange: "€1,500-€3,500",
    eventsHosted: 40,
    brandCollaborations: 4,
    image: photographyParisImg,
    memberDrivers: ["Expression & Identity", "Practice & Discipline", "Access & Opportunity"],
    communityValues: ["Authenticity & Transparency", "Innovation & Progress", "Inclusivity & Community"],
    tribes: [
      { tribe: "Culture", percentage: 55, microTribes: [{ name: "Contemporary Art", percentage: 45 }, { name: "Film", percentage: 30 }, { name: "Design", percentage: 25 }] },
      { tribe: "Tech", percentage: 20, microTribes: [{ name: "Product & Innovation", percentage: 60 }, { name: "Artificial Intelligence", percentage: 40 }] },
      { tribe: "Travel & Adventure", percentage: 15, microTribes: [{ name: "Cultural Travel", percentage: 60 }, { name: "Adventure Travel", percentage: 40 }] },
      { tribe: "Relationships & Social Life", percentage: 10, microTribes: [{ name: "Social Events", percentage: 100 }] },
    ],
    intentionDrivers: [
      { name: "Exploration & Novelty", percentage: 35 },
      { name: "Identity & Belonging", percentage: 30 },
      { name: "Performance & Improvement", percentage: 20 },
      { name: "Balance & Well-being", percentage: 15 },
    ],
    consumptionPatterns: [
      { name: "Experimental", percentage: 30 },
      { name: "Premium", percentage: 25 },
      { name: "Aspirational", percentage: 20 },
      { name: "Social", percentage: 15 },
      { name: "Functional", percentage: 10 },
    ],
  },
  {
    id: "coffee-lovers-marseille",
    name: "Coffee Lovers Marseille",
    location: "Marseille",
    geographicScope: "City-wide",
    governanceModel: "Founder-Led",
    stage: "Emerging",
    platforms: ["Instagram", "WhatsApp"],
    activeMembers: 100,
    totalReach: 3200,
    networkDensity: "190K",
    avgEventAttendance: "20–40",
    eventsPerYear: 18,
    theme: ["Fine Dining", "Home Cooking"],
    memberCount: 175,
    fitScore: 73,
    participationRate: 77,
    eventFrequency: "Monthly",
    description: "Marseille's specialty coffee community, from home brewers to professionals.",
    mission: "Celebrating coffee culture in Marseille through tastings, barista workshops, and café tours.",
    demographics: { ageRange: "25-45", genderRatio: "50% F / 50% M", interests: ["Coffee", "Food", "Cafés", "Artisanal products"] },
    activationCapabilities: ["Event Sponsorship", "Product Gifting", "Co-Creation Workshops", "Product Testing", "Affiliate Program"],
    typicalEventSize: "20-40",
    leadTime: "2-3 weeks",
    budgetRange: "€800-€2,000",
    eventsHosted: 18,
    brandCollaborations: 3,
    image: coffeeMarseilleImg,
    memberDrivers: ["Practice & Discipline", "Connection & Belonging", "Expression & Identity"],
    communityValues: ["Authenticity & Transparency", "Luxury & Exclusivity", "Sustainability & Ethics"],
    tribes: [
      { tribe: "Food", percentage: 65, microTribes: [{ name: "Fine Dining", percentage: 30 }, { name: "Home Cooking", percentage: 40 }, { name: "Plant Based Diets", percentage: 15 }, { name: "Wine", percentage: 15 }] },
      { tribe: "Lifestyle", percentage: 25, microTribes: [{ name: "Health", percentage: 50 }, { name: "Mental Health", percentage: 30 }, { name: "Pets & Animals", percentage: 20 }] },
      { tribe: "Travel & Adventure", percentage: 10, microTribes: [{ name: "Cultural Travel", percentage: 100 }] },
    ],
    intentionDrivers: [
      { name: "Exploration & Novelty", percentage: 35 },
      { name: "Identity & Belonging", percentage: 25 },
      { name: "Balance & Well-being", percentage: 25 },
      { name: "Performance & Improvement", percentage: 15 },
    ],
    consumptionPatterns: [
      { name: "Premium", percentage: 30 },
      { name: "Experimental", percentage: 25 },
      { name: "Social", percentage: 20 },
      { name: "Habit-Driven", percentage: 15 },
      { name: "Aspirational", percentage: 10 },
    ],
  },
  {
    id: "entrepreneurs-paris",
    name: "Entrepreneurs Club Paris",
    location: "Paris",
    geographicScope: "National",
    governanceModel: "Founder-Led",
    stage: "Established",
    platforms: ["Slack", "LinkedIn", "Instagram", "Meetup"],
    activeMembers: 260,
    totalReach: 18000,
    networkDensity: "1.5M",
    avgEventAttendance: "60–100",
    eventsPerYear: 24,
    theme: ["Startups", "Product & Innovation"],
    memberCount: 520,
    fitScore: 84,
    participationRate: 69,
    eventFrequency: "Monthly",
    description: "Paris's premier community for startup founders and aspiring entrepreneurs.",
    mission: "Supporting entrepreneurs on their journey with mentorship, networking, and resources.",
    demographics: { ageRange: "25-45", genderRatio: "35% F / 65% M", interests: ["Entrepreneurship", "Startups", "Investment", "Innovation"] },
    activationCapabilities: ["Event Sponsorship", "Product Gifting", "Branded Content", "Product Testing", "Affiliate Program", "Ambassador Program"],
    typicalEventSize: "60-100",
    leadTime: "4-6 weeks",
    budgetRange: "€3,000-€7,000",
    eventsHosted: 24,
    brandCollaborations: 6,
    image: entrepreneursParisImg,
    memberDrivers: ["Access & Opportunity", "Progress & Growth", "Connection & Belonging"],
    communityValues: ["Innovation & Progress", "Performance & Excellence", "Luxury & Exclusivity"],
    tribes: [
      { tribe: "Tech", percentage: 50, microTribes: [{ name: "Startups", percentage: 50 }, { name: "Artificial Intelligence", percentage: 20 }, { name: "Product & Innovation", percentage: 15 }, { name: "Web3", percentage: 15 }] },
      { tribe: "Learning & Growth", percentage: 30, microTribes: [{ name: "Career Development", percentage: 40 }, { name: "Skill Building", percentage: 35 }, { name: "Personal Development", percentage: 25 }] },
      { tribe: "Relationships & Social Life", percentage: 20, microTribes: [{ name: "Social Events", percentage: 100 }] },
    ],
    intentionDrivers: [
      { name: "Performance & Improvement", percentage: 35 },
      { name: "Exploration & Novelty", percentage: 25 },
      { name: "Identity & Belonging", percentage: 25 },
      { name: "Impact & Ethics", percentage: 15 },
    ],
    consumptionPatterns: [
      { name: "Aspirational", percentage: 30 },
      { name: "Premium", percentage: 25 },
      { name: "Experimental", percentage: 20 },
      { name: "Pragmatic", percentage: 15 },
      { name: "Social", percentage: 10 },
    ],
  },
];

export const getFitScoreClass = (score: number): string => {
  if (score >= 70) return "fit-score-high";
  if (score >= 35) return "fit-score-medium";
  return "fit-score-low";
};
